import { createAuthTokenManager } from "@/utils/di";
import { TgBotTracker } from "@/utils/tg-bot/TgBotTracker";
import { v4 as uuidV4 } from "uuid";
import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
} from "vue-router";
import authRoutes from "../components/auth/router";
import dashboardRoutes from "../components/dashboard/router";
import { Component } from "vue";

const routes = [
  {
    path: "/",
    component: (): Promise<Component> =>
      import("../components/public/Main.vue"),
    name: "home",
    meta: { requiresAuth: false },
  },
  {
    path: "/documents",
    component: (): Promise<Component> =>
      import("../components/public/Documents.vue"),
    name: "publicDocuments",
    meta: { requiresAuth: false },
  },
  {
    path: "/faq",
    component: (): Promise<Component> => import("../components/public/Faq.vue"),
    name: "publicFaq",
    meta: { requiresAuth: false },
  },
  {
    path: "/page/:slug",
    component: (): Promise<Component> =>
      import("../components/public/StaticPage.vue"),
    name: "publicStaticPage",
    meta: { requiresAuth: false },
  },
  {
    path: "/verification/:verificationCode",
    component: (): Promise<Component> =>
      import("../components/public/Verification.vue"),
    name: "verification",
    meta: { requiresAuth: false },
  },
  {
    path: "/auth",
    component: (): Promise<Component> => import("../components/auth/Auth.vue"),
    children: authRoutes,
  },
  {
    path: "/dashboard",
    component: (): Promise<Component> =>
      import("../components/dashboard/Dashboard.vue"),
    children: dashboardRoutes,
    name: "dashboard",
  },
  {
    path: "/:pathMatch(.*)*",
    component: (): Promise<Component> =>
      import("../components/errors/components/Page404.vue"),
    name: "pageNotFound",
    // Without this the guard below treats a mistyped URL as a protected route:
    // a logged-out visitor was sent to the login form instead of the 404 page,
    // and `redirectTo` was primed with the bad path, so logging in bounced them
    // straight back to it.
    meta: { requiresAuth: false },
  },
];

function addVisit(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): void {
  try {
    const basePath = window.location.protocol + "//" + window.location.host;
    const data = localStorage.getItem("visits");
    const visits: Array<any> = data ? JSON.parse(data) : [];
    visits.push({
      uuid: uuidV4(),
      userAgent: window.navigator.userAgent,
      url: basePath + to.fullPath,
      referrer:
        from.fullPath && from.fullPath !== "/"
          ? basePath + from.fullPath
          : document.referrer,
      createdAt: new Date().toISOString(),
    });
    localStorage.setItem("visits", JSON.stringify(visits));
  } catch (error) {
    console.error(error);
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  TgBotTracker.capture(to.query);
  addVisit(to, from);
  if (
    (to.meta && to.meta.requiresAuth === false) ||
    (await createAuthTokenManager().has())
  ) {
    next();
  } else {
    sessionStorage.setItem("redirectTo", to.path);
    next({ name: "login" });
  }
});

export default router;
